import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avUsageCheckKeys } from './queryKeys';

export interface AnalyticalVariableUsageItem {
  usageType: 'cohort' | 'analytical_dataset' | 'analytical_variable';
  id: string;
  name: string;
  /** Project the usage lives in; an organization-scoped variable's usages can span projects. */
  projectId: string | null;
  versionId: string | null;
  status?: string | null;
}

export interface AnalyticalVariableUsageResponse {
  analyticalVariableId: string;
  isUsed: boolean;
  usageCount: number;
  usages: AnalyticalVariableUsageItem[];
  /**
   * OR-6234: the variable has an open (unsaved) edit. Not a gate on Delete;
   * the confirmation copy uses it to say the edit goes with the variable.
   */
  editInProgress: boolean;
}

/**
 * Everything that still uses an analytical variable — cohorts (saved or with a
 * pending edit), analytical datasets, other variables — from
 * ``GET /analytical-variables/{id}/usage``. The delete dialog reads it on open
 * so it can name what blocks the delete before the researcher clicks (OR-6234
 * AC 3). Answers for a never-saved Draft too (nothing can use one).
 *
 * Not cached across opens: the answer must reflect the moment the dialog opens,
 * and the server re-checks before writing anyway. Its key sits outside
 * ``avKeys.all`` — see ``avUsageCheckKeys``.
 *
 * Asks with ``require_complete``: a partial answer would let the dialog offer
 * Delete on searches that did not all run. The server answers 503 instead, which
 * the dialog reports as "could not check" rather than as "nothing uses it".
 */
export const useAnalyticalVariableUsageQuery = (
  analyticalVariableId: string | null,
  options?: { enabled?: boolean }
): UseQueryResult<ObjectResponse<AnalyticalVariableUsageResponse>> =>
  useQuery({
    queryKey: avUsageCheckKeys.complete(analyticalVariableId ?? ''),
    queryFn: () =>
      authFetch<ObjectResponse<AnalyticalVariableUsageResponse>>(
        `/analytical-variables/${encodeURIComponent(
          analyticalVariableId ?? ''
        )}/usage?require_complete=true`
      ),
    enabled: options?.enabled !== false && !!analyticalVariableId,
    staleTime: 0,
    gcTime: 0,
  });
